// @ts-nocheck
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  Modal,
  Typography,
  CircularProgress,
  Checkbox,
  useMediaQuery,
  Tabs,
  Tab,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  createClientAction,
  updateClientAction,
} from "../../../store/actions/client";
import {
  phoneNumberForApi,
  phoneNumberFormat,
} from "../../../utils/methods/formatters";
import { USER_ROLES } from "../../../staticData/userRoles";
import { getCssVariableHandler } from "../../../utils/styles/getCssVariables";
import AddressAutocomplete from "../../../components/Custom/react-google-maps/AddressAutoComplete";
import { formatAddress } from "../../../utils/methods/other/otherUtils";
import {
  CLIENT_EMAIL,
  CLIENT_EMAIL_LABEL,
} from "../../../utils/constants/client";
import {
  CustomChip,
  CustomIconButton,
  SmallAddButton,
} from "../../../components/Custom/CustomIconButton";
import { Add, CheckBox } from "@mui/icons-material";
import { alpha, display, maxHeight, padding } from "@mui/system";
import { AddInvoiceEamils } from "./AddInvoiceEmails";
import { CustomTooltip } from "../../../components/Tooltip/ClientTool";
import CompaniesAutocompelte from "../../../components/Custom/CompaniesAutocompelte";
import MergeClientBilling from "./MergeClientBilling";
import { isUserRole, isActiveRole } from "../../../utils/methods/storeFunc";
import UnderlineButton from "../../../components/Custom/UnderlineButton";
import InfoHover from "../../../components/Tooltip/InfoHover";
import ClientEmailPreferencesSection from "./ClientEmailPreferencesSection";
import { alertDialogAciton } from "../../../store/actions/auth";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  boxShadow: 24,
  p: 4,
  minWidth: "700px",
  borderRadius: "16px",
  padding: "20px",
  border: "2px solid #D175B6",
  backgroundColor: "#000",
  zIndex: 9,
  maxHeight: "95vh",
  overflowY: "auto",

  "@media (max-width: 600px)": {
    minWidth: "0px",
    width: "97vw",
    maxHeight: "95vh",
    overflowY: "auto",
  },
};

export const titleSx = {
  fontSize: "1rem",
  color: getCssVariableHandler({ variableName: "--primary-pink-hex" }),
};

export default function AddClientModal({
  heading,
  open,
  setEditingData,
  setOpen,
  data,
  isProfile = false,
  afterSaveCall = () => {},
  providedDataValue = null,
}) {
  const addInvoiceInit = useMemo(() => {
    return {
      status: false,
      type: CLIENT_EMAIL.estimate,
    };
  }, []);
  const { userId, userRole, isBusinessProfile } = useSelector(
    (state) => state.auth,
  );
  const { isLoading } = useSelector((state) => state.client);
  const dispatch = useDispatch();
  const [showPass, setShowPass] = React.useState(false);
  const [addInvoice, setAddInvoice] = useState(addInvoiceInit);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showSearchBusiness, setShowSearchBusiness] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const isSmallScreen = useMediaQuery((theme) =>
    theme?.breakpoints?.down("sm"),
  );
  // Admin / superAdmin editing an existing client (not the client's own profile):
  // shows the client's email preferences section, saved together with Submit.
  const emailPrefsRef = useRef(null);
  const showClientEmailPrefs =
    Boolean(data?._id || data?.id) &&
    !isProfile &&
    isActiveRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN);

  const initialValues = {
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    isBusinessProfile: isActiveRole(USER_ROLES.CLIENT)
      ? isBusinessProfile
      : false,
    existingBusinessProfileId: null,
    companyName: "",
    isSameAddress: true,
    companyAddress: "",
    COIAddress: "",
    emails: {
      estimateEmails: [],
      invoiceEmails: [],
      depositInvoiceEmails: [],
    },
  };

  React.useEffect(() => {
    if (data?.id || data?._id) {
      formik.setValues({
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        email: data?.email || "",
        mobile: phoneNumberFormat(data?.mobile) || "",
        isBusinessProfile: data?.isBusinessProfile || false,
        existingBusinessProfileId: data?.businessProfileId?._id,
        companyName:
          data?.businessProfileId?.companyName || data?.companyName || "",
        // `isSameAddress` lives at the ROOT of the business profile, not under
        // clientDetails — and `|| true` could never yield false. With the backend now
        // returning the populated profile, that would re-check the box on every edit and
        // the save would overwrite a distinct COI address with the company address.
        isSameAddress: data?.businessProfileId?.isSameAddress ?? true,
        companyAddress: data?.businessProfileId?.clientDetails?.companyAddress ?? "",
        COIAddress: data?.businessProfileId?.clientDetails?.COIAddress ?? "",
        emails: {
          estimateEmails:
            data?.businessProfileId?.clientDetails?.emails?.estimateEmails?.map(
              (e) => ({
                ...e,
                estimateMobile: phoneNumberFormat(e?.estimateMobile),
              }),
            ) || [],
          invoiceEmails:
            data?.businessProfileId?.clientDetails?.emails?.invoiceEmails?.map(
              (e) => ({
                ...e,
                invoiceMobile: phoneNumberFormat(e?.invoiceMobile),
              }),
            ) || [],
          depositInvoiceEmails:
            data?.businessProfileId?.clientDetails?.emails?.depositInvoiceEmails?.map(
              (e) => ({
                ...e,
                depositInvoiceMobile: phoneNumberFormat(
                  e?.depositInvoiceMobile,
                ),
              }),
            ) || [],
        },
      });
    } else {
      formik.setValues(initialValues);
    }
  }, [data]);

  const handleClose = () => {
    setEditingData(null);
    formik.setTouched({});
    formik.setValues(data ? data : initialValues);
    setOpen(false);
  };

  const togglePasswordHide = () => {
    setShowPass(!showPass);
  };

  const emailSchema = (type) => {
    const emailName = `${type}Email`;
    const mobileName = `${type}Mobile`;
    return Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .max(200, "Name should have at most 200 characters"),
        [emailName]: Yup.string()
          .trim()
          .email("Invalid email")
          .required("Email required"),
        [mobileName]: Yup.string()
          .matches(/^\d{3}-\d{3}-\d{4}$/, { message: "Phone number must be of 10 digit", excludeEmptyString: true }),
      }),
    );
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .max(100, "First Name should have at most 100 characters!")
      .required("First name is required"),
    lastName: Yup.string()
      .trim()
      .max(100, "Last Name should have at most 100 characters!")
      .required("Last name is required "),
    mobile: Yup.string()
      .matches(/^\d{3}-\d{3}-\d{4}$/, "Phone number must be of 10 digit")
      .required("Phone number required"),
    email: Yup.string()
      .trim()
      .email("Invalid email")
      .required("Email required"),
    isBusinessProfile: Yup.boolean(),
    companyName: Yup.string()
      .trim()
      .optional()
      .max(300, "Company name should have at most 300 character"),
    companyAddress: Yup.string()
      .trim()
      .optional()
      .max(300, "Company address should have at most 300 character"),
    isSameAddress: Yup.boolean(),
    existingBusinessProfileId: Yup.string()
      .nullable()
      .when("isBusinessProfile", {
        is: false,
        then: (schema) => {
          schema.optional().nullable().notRequired();
        },
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    COIAddress: Yup.string()
      .trim()
      .optional()
      .max(300, "COI address should have at most 300 characters"),
    emails: Yup.object()
      .shape({
        estimateEmails: emailSchema(CLIENT_EMAIL.estimate),
        invoiceEmails: emailSchema(CLIENT_EMAIL.invoice),
        depositInvoiceEmails: emailSchema(CLIENT_EMAIL.depositInvoice),
      })
      .optional(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { password, ...rest } = values;
      const payload = {
        ...rest,
        email: values?.email?.trim()?.toLowerCase(),
        forceAdd: false,
        mobile: phoneNumberForApi(values?.mobile),
        sentEmailToClientOnSignup: sendEmail,
        clientDetails: {
          emails: {
            depositInvoiceEmails: values?.emails?.depositInvoiceEmails?.map(
              (e) => ({
                ...e,
                depositInvoiceMobile: phoneNumberForApi(
                  e?.depositInvoiceMobile,
                ),
              }),
            ),
            invoiceEmails: values?.emails?.invoiceEmails?.map((e) => ({
              ...e,
              invoiceMobile: phoneNumberForApi(e?.invoiceMobile),
            })),
            estimateEmails: values?.emails?.estimateEmails?.map((e) => ({
              ...e,
              estimateMobile: phoneNumberForApi(e?.estimateMobile),
            })),
          },
          companyAddress: values?.companyAddress,
          COIAddress: values?.COIAddress,
        },
        createdBy: userId,
      };
      // const formData = new FormData();
      //
      // // Append all fields to formData
      // Object.keys(payload).forEach((key) => {
      //   if (payload[key] !== null && payload[key] !== undefined) {
      //     if (
      //       typeof payload[key] === "object" &&
      //       !(payload[key] instanceof File)
      //     ) {
      //       formData.append(key, JSON.stringify(payload[key]));
      //     } else {
      //       formData.append(key, payload[key]);
      //     }
      //   }
      // });

      if (data?._id || data?.id) {
        // Email preferences go first: if they fail (e.g. a rule from the backend),
        // the modal stays open with the message and nothing else is sent.
        if (showClientEmailPrefs && emailPrefsRef.current) {
          const saved = await emailPrefsRef.current.save();
          if (!saved?.ok) {
            dispatch(
              alertDialogAciton({
                open: true,
                title: "Error",
                message: saved?.message || "Could not save the client's email preferences.",
              }),
            );
            return;
          }
        }
        // payload.clientDetails.companyAddress = payload?.companyAddress;
        // payload.clientDetails.COIAddress = payload?.COIAddress;
        dispatch(
          updateClientAction(
            {
              id: data?._id,
              data: payload,
              isProfile,
            },
            () => {
              afterSaveCall();
              handleClose();
            },
          ),
        );
      } else {
        dispatch(
          createClientAction(payload, () => {
            afterSaveCall();
            handleClose();
          }),
        );
      }
    },
  });

  const {
    values,
    touched,
    handleBlur,
    setFieldValue,
    setValues,
    handleChange,
    errors,
    handleSubmit,
  } = formik;

  useEffect(() => {
    if (values?.existingBusinessProfileId) {
      setShowSearchBusiness(true);
    } else {
      setShowSearchBusiness(false);
    }
  }, [values?.existingBusinessProfileId]);

  function setCOIAddress(address) {
    if (isBillingReadOnly) return;
    setFieldValue("COIAddress", formatAddress(address));
  }
  function setCompanyAddress(address) {
    if (isBillingReadOnly) return;
    setFieldValue("companyAddress", formatAddress(address));
  }

  const handleMobileChange = (event) => {
    const input = event.target.value;
    const formattedInput = input
      .replace(/\D/g, "")
      .replace(/(\d{1,3})(\d{1,3})?(\d{1,4})?/, function (_, p1, p2, p3) {
        let formattedNumber = "";
        if (p1) {
          formattedNumber += p1;
        }
        if (p2) {
          formattedNumber += "-" + p2;
        }
        if (p3) {
          formattedNumber += "-" + p3;
        }
        return formattedNumber;
      });
    handleChange({
      target: {
        name: "mobile",
        value: formattedInput,
      },
    });
  };

  function addInvoiceAppendFunc({ values, type }) {
    if (type == "all") {
      const estimateEmails = `${CLIENT_EMAIL.estimate}Emails`;
      const depositInvoiceEmails = `${CLIENT_EMAIL.depositInvoice}Emails`;
      const invoiceEmails = `${CLIENT_EMAIL.invoice}Emails`;
      setValues((prev) => ({
        ...prev,
        emails: {
          ...prev?.emails,
          estimateEmails: [
            ...(prev?.emails?.[estimateEmails] || []),
            {
              estimateEmail: values?.allEmail,
              estimateMobile: values?.allMobile,
              name: values?.name,
            },
          ],
          [depositInvoiceEmails]: [
            ...(prev?.emails?.[depositInvoiceEmails] || []),
            {
              depositInvoiceEmail: values?.allEmail,
              depositInvoiceMobile: values?.allMobile,
              name: values?.name,
            },
          ],
          [invoiceEmails]: [
            ...(prev?.emails?.[invoiceEmails] || []),
            {
              invoiceEmail: values?.allEmail,
              invoiceMobile: values?.allMobile,
              name: values?.name,
            },
          ],
        },
      }));
      return;
    }
    const key = `${type}Emails`;
    setValues((prev) => ({
      ...prev,
      emails: {
        ...prev?.emails,
        [key]: [...(prev?.emails?.[key] || []), values],
      },
    }));
  }

  function handleTabChange(_, newValue) {
    if (data?._id || data?.id) return;
    setFieldValue("isBusinessProfile", newValue == 0 ? true : false);
    setFieldValue("existingBusinessProfileId", null);
  }

  const isBillingReadOnly = useMemo(() => {
    if (isUserRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN)) return false;
    if (isActiveRole(USER_ROLES.CLIENT) && !isBusinessProfile) return true;
    return false;
  }, [isBusinessProfile]);

  const showSearch = useMemo(() => {
    if (!isBillingReadOnly && !isProfile) {
      if (values?.isBusinessProfile) {
        return showSearchBusiness;
      } else {
        return true;
      }
    }
    return (!isBillingReadOnly || showSearchBusiness) && !isProfile;
  }, [
    isBillingReadOnly,
    isProfile,
    showSearchBusiness,
    values?.isBusinessProfile,
  ]);

  return (
    <Modal
      className="modelBox"
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} className="custom-scroll-design">
        <Typography
          className="model-title"
          id="modal-modal-title"
          variant="h6"
          component="h2"
          marginBottom="20px"
        >
          {heading || "Add Client"}
        </Typography>
        {/* {!isActiveRole(USER_ROLES.CLIENT) && ( */}
        {/*   <Box */}
        {/*     sx={{ */}
        {/*       width: "100%", */}
        {/*       mb: 2, */}
        {/*       // display: values?.isBusinessProfile ? "inherit" : "none", */}
        {/*     }} */}
        {/*   > */}
        {/*     <Tabs */}
        {/*       value={values?.isBusinessProfile ? 0 : 1} */}
        {/*       onChange={handleTabChange} */}
        {/*       TabScrollButtonProps={{ */}
        {/*         style: { */}
        {/*           color: `${getCssVariableHandler({ */}
        {/*             variableName: "--primary-pink-hex", */}
        {/*           })}`, */}
        {/*         }, */}
        {/*       }} */}
        {/*       aria-label="basic tabs example" */}
        {/*       variant="scrollable" */}
        {/*       sx={{ */}
        {/*         width: "100%", */}
        {/*         border: "1px solid #D175B6", */}
        {/*         overflow: "hidden", */}
        {/*         display: "inline-flex", */}
        {/*         justifyContent: "space-between", */}
        {/*         borderRadius: "8px", */}
        {/*         "& button": { */}
        {/*           textTransform: "none", */}
        {/*           width: "50%", */}
        {/*           color: "#D175B6", */}
        {/*           "&.Mui-selected": { */}
        {/*             backgroundColor: "#D175B6", */}
        {/*           }, */}
        {/*           "& span": { */}
        {/*             display: "none", */}
        {/*           }, */}
        {/*           cursor: */}
        {/*             data?._id || data?.id */}
        {/*               ? "not-allowed !important" */}
        {/*               : "pointer !important", */}
        {/*         }, */}
        {/*         "& .MuiTabs-indicator": { */}
        {/*           display: "none", */}
        {/*         }, */}
        {/*       }} */}
        {/*     > */}
        {/*       <Tab label="Business Profile" id={0} /> */}
        {/*       <Tab label="Representative Profile" id={1} /> */}
        {/*     </Tabs> */}
        {/*   </Box> */}
        {/* )} */}
        <Box
          sx={{
            border: `1px solid ${getCssVariableHandler({
              variableName: "--primary-pink-hex",
            })}`,
            borderRadius: "0.5rem",
            p: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography sx={titleSx}>Personal Details:</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className="new-form-box">
                <InputLabel>First Name</InputLabel>
                <input
                  name="firstName"
                  value={values.firstName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="First Name"
                />
                {errors.firstName && touched.firstName && (
                  <Typography variant="subtitle2" color="red">
                    {errors.firstName}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className="new-form-box">
                <InputLabel>Last Name</InputLabel>
                <input
                  name="lastName"
                  value={values.lastName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Last Name"
                />
                {errors.lastName && touched.lastName && (
                  <Typography variant="subtitle2" color="red">
                    {errors.lastName}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className="new-form-box">
                <InputLabel>Phone Number</InputLabel>
                <input
                  name="mobile"
                  value={values.mobile}
                  onBlur={handleBlur}
                  onChange={handleMobileChange}
                  placeholder="XXX-XXX-XXXX"
                />
                {errors.mobile && touched.mobile && (
                  <Typography variant="subtitle2" color="red">
                    {errors.mobile}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className="new-form-box">
                <InputLabel>Email</InputLabel>
                <input
                  name="email"
                  value={values.email}
                  readOnly={data}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="abc@xyz.com"
                />
                {errors.email && touched.email && (
                  <Typography variant="subtitle2" color="red">
                    {errors.email}
                  </Typography>
                )}
              </Box>
            </Grid>

            {!isActiveRole(USER_ROLES.CLIENT) && (
              <Grid item xs={12}>
                <InputLabel>Profile Type</InputLabel>
                <FormControl
                  sx={{
                    width: "100%",
                  }}
                >
                  <RadioGroup
                    name="profileType"
                    row
                    sx={{
                      flexWrap: "wrap",
                      ...(data?.id || data?._id ? {} : {}),
                    }}
                    value={values?.isBusinessProfile ? 0 : 1}
                    onChange={(e) => {
                      // if (data?.id || data?._id) return;
                      const value = e?.target?.value;
                      if (value == 0) {
                        setFieldValue("isBusinessProfile", true);
                      } else {
                        setFieldValue("isBusinessProfile", false);
                      }
                    }} // your handler
                  >
                    <FormControlLabel
                      value="0"
                      control={
                        <Radio
                          sx={{
                            color: getCssVariableHandler({
                              variableName: "--primary-pink-hex",
                            }),
                            "&.Mui-checked": {
                              color: getCssVariableHandler({
                                variableName: "--primary-pink-hex",
                              }),
                            },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            ...(data?.id || data?._id
                              ? { cursor: "not-allowed" }
                              : {}),
                          }}
                        >
                          Primary Contact
                          {/* NOTE: update the info icon with black i and white bg */}
                          <InfoHover
                            title="Please select this profile, if you are primary contact of your comany or identifies yourself  as a business owner or you are an individual."
                            infoStyles={{
                              color: getCssVariableHandler({
                                variableName: "--white-rgb",
                              }),
                            }}
                            isBgFilled
                          />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="1"
                      control={
                        <Radio
                          sx={{
                            color: getCssVariableHandler({
                              variableName: "--primary-pink-hex",
                            }),
                            "&.Mui-checked": {
                              color: getCssVariableHandler({
                                variableName: "--primary-pink-hex",
                              }),
                            },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            ...(data?.id || data?._id
                              ? { cursor: "not-allowed" }
                              : {}),
                          }}
                        >
                          Representative Profile
                          <InfoHover
                            title="This profile identifies you as a representative of a registered business."
                            infoStyles={{
                              color: getCssVariableHandler({
                                variableName: "--white-rgb",
                              }),
                            }}
                            isBgFilled
                          />
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            border: `1px solid ${getCssVariableHandler({
              variableName: "--primary-pink-hex",
            })}`,
            borderRadius: "0.5rem",
            p: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={titleSx}>Company Details:</Typography>
            </Grid>
            {values?.isBusinessProfile && !isProfile && (
              <Grid item xs={12} sx={{ paddingTop: "2px !important" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showSearchBusiness}
                      sx={{
                        color: "white",
                      }}
                      onChange={(e) => {
                        const val = e.target.checked;
                        setShowSearchBusiness(val);
                        if (!val) {
                          setFieldValue("existingBusinessProfileId", null);
                          setFieldValue("companyName", "");
                          setFieldValue("companyAddress", "");
                          setFieldValue("COIAddress", "");
                          setFieldValue("isSameAddress", true);
                          setFieldValue("emails", {
                            depositInvoiceEmails: [],
                            estimateEmails: [],
                            invoiceEmails: [],
                          });
                        }
                      }}
                    />
                  }
                  label="Select Existing Company"
                  sx={{
                    margin: 0,
                    "& .MuiFormControlLabel-label": {},
                  }}
                />
              </Grid>
            )}
            {showSearch ? (
              <Grid
                item
                xs={12}
                sx={{
                  paddingTop: !values?.isBusinessProfile
                    ? "1rem !important"
                    : "2px !important",
                }}
              >
                <Box className="new-form-box">
                  {/* <InputLabel>Select Company</InputLabel> */}
                  <CompaniesAutocompelte
                    placeholder={"Type to search companies"}
                    noOptionsText={"No Companies Found"}
                    // value={values?.existingBusinessProfileId}
                    name={"existingBusinessProfileId"}
                    onBlur={handleBlur}
                    fetchOptions
                    getOptionLabel={(op) => op?.companyName || ""}
                    borderColor={"#6d6c6c"}
                    onChange={(_, newVal) => {
                      // `?? ""`: without it a business profile with no address left the
                      // controlled input at undefined (React warns and the field sticks
                      // to its previous value). COIAddress mirrors companyAddress when
                      // isSameAddress — that is what the BE stores (clientDetails.COIAddress).
                      const sameAddress = newVal?.isSameAddress ?? true;
                      const companyAddress =
                        newVal?.clientDetails?.companyAddress ?? "";
                      setFieldValue("companyName", newVal?.companyName ?? "");
                      setFieldValue("isSameAddress", sameAddress);
                      setFieldValue("companyAddress", companyAddress);
                      setFieldValue(
                        "COIAddress",
                        sameAddress
                          ? companyAddress
                          : (newVal?.clientDetails?.COIAddress ?? ""),
                      );
                      const emails = {
                        depositInvoiceEmails:
                          newVal?.clientDetails?.emails?.depositInvoiceEmails?.map(
                            (i) => ({
                              ...i,
                              depositInvoiceMobile: phoneNumberFormat(
                                i?.depositInvoiceMobile,
                              ),
                            }),
                          ),
                        estimateEmails:
                          newVal?.clientDetails?.emails?.estimateEmails?.map(
                            (i) => ({
                              ...i,
                              estimateMobile: phoneNumberFormat(
                                i?.estimateMobile,
                              ),
                            }),
                          ),
                        invoiceEmails:
                          newVal?.clientDetails?.emails?.invoiceEmails?.map(
                            (i) => ({
                              ...i,
                              invoiceMobile: phoneNumberFormat(
                                i?.invoiceMobile,
                              ),
                            }),
                          ),
                      };
                      setFieldValue("emails", emails);
                      setFieldValue("existingBusinessProfileId", newVal?._id);
                    }}
                  />
                  {errors.existingBusinessProfileId &&
                    touched.existingBusinessProfileId && (
                      <Typography variant="subtitle2" color="red">
                        {errors.existingBusinessProfileId}
                      </Typography>
                    )}
                </Box>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Box className="new-form-box">
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <InputLabel>Company Name</InputLabel>
                  {providedDataValue && (
                    <UnderlineButton
                      title={"Compare Billing Informations"}
                      onClick={() => setShowMergeModal(true)}
                      styles={{
                        fontSize: "1rem",
                      }}
                    />
                  )}
                </Grid>

                <input
                  name="companyName"
                  value={values.companyName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Company Name"
                  readOnly={isBillingReadOnly}
                />
                {errors.companyName && touched.companyName && (
                  <Typography variant="subtitle2" color="red">
                    {errors.companyName}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className="new-form-box">
                <InputLabel>Company Address</InputLabel>
                <AddressAutocomplete
                  setAddress={setCompanyAddress}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  value={values.companyAddress}
                  fullWidth
                  placeholder="Type company address"
                  name="companyAddress"
                  onChange={handleChange}
                  readOnly={isBillingReadOnly}
                />
                {errors.companyAddress && touched.companyAddress && (
                  <Typography variant="subtitle2" color="red">
                    {errors.companyAddress}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "10px",
                  }}
                >
                  <Checkbox
                    name={"isSameAddress"}
                    checked={values?.isSameAddress}
                    onChange={(e) => {
                      if (isBillingReadOnly) return;
                      setValues((values) => {
                        return {
                          ...values,
                          isSameAddress: e?.target?.checked,
                        };
                      });
                    }}
                    onBlur={handleBlur}
                    sx={{
                      color: getCssVariableHandler({
                        variableName: "--font-rgb",
                      }),
                      padding: "0",
                    }}
                  />
                  <InputLabel
                    sx={{
                      marginBottom: "0 !important",
                    }}
                  >
                    Use same address for COI as company Address
                  </InputLabel>
                </Box>
              </Box>
            </Grid>

            {!values?.isSameAddress && (
              <Grid item xs={12}>
                <Box className="new-form-box">
                  <InputLabel>COI Address</InputLabel>
                  <AddressAutocomplete
                    setAddress={setCOIAddress}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    value={values.COIAddress}
                    fullWidth
                    placeholder="Type COI address"
                    name="COIAddress"
                    onChange={handleChange}
                    readOnly={isBillingReadOnly}
                  />
                  {errors.COIAddress && touched.COIAddress && (
                    <Typography variant="subtitle2" color="red">
                      {errors.COIAddress}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            border: `1px solid ${getCssVariableHandler({
              variableName: "--primary-pink-hex",
            })}`,
            borderRadius: "0.5rem",
            p: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Typography sx={{ ...titleSx, display: "inline" }}>
                Billing Informations:
              </Typography>

              {!isBillingReadOnly && (
                <SmallAddButton
                  onClick={() => {
                    setAddInvoice({ status: true, type: "all" });
                  }}
                  title={"Add to all"}
                />
              )}
            </Grid>

            <div style={{ paddingLeft: "1rem", paddingTop: "1rem" }}>
              {addInvoice?.status && addInvoice?.type == "all" ? (
                <AddInvoiceEamils
                  open={addInvoice?.status}
                  handleClose={() => setAddInvoice(addInvoiceInit)}
                  type={"all"}
                  addEmail={addInvoiceAppendFunc}
                />
              ) : null}
            </div>
            <Typography sx={{ fontSize: "0.85rem", opacity: 0.8, mb: 1 }}>
              Save contacts once for this company. Each document starts with its own list, including existing shows. Add accounting contacts only to Deposit Invoice and Invoice when they should not receive estimates.
            </Typography>
            {[
              CLIENT_EMAIL.estimate,
              CLIENT_EMAIL.depositInvoice,
              CLIENT_EMAIL.invoice,
            ]?.map((item) => {
              const key = `${item}Emails`;
              return (
                <Grid item xs={12} key={item}>
                  <Typography
                    sx={{
                      ...titleSx,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      color: getCssVariableHandler({
                        variableName: "--white-rgb",
                      }),
                      paddingBottom: 1,
                    }}
                  >
                    {CLIENT_EMAIL_LABEL?.[item]} Profiles:
                    {!isBillingReadOnly && (
                      <SmallAddButton
                        onClick={() => {
                          setAddInvoice({ status: true, type: item });
                        }}
                      />
                    )}
                  </Typography>
                  {addInvoice?.status && addInvoice?.type == item ? (
                    <AddInvoiceEamils
                      open={addInvoice?.status}
                      handleClose={() => setAddInvoice(addInvoiceInit)}
                      type={addInvoice?.type}
                      existingContacts={values?.emails?.[`${addInvoice?.type}Emails`] || []}
                      addEmail={addInvoiceAppendFunc}
                    />
                  ) : null}
                  {values?.emails?.[key]?.length == 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Typography
                        sx={{
                          border: `1px solid ${getCssVariableHandler({
                            variableName: "--primary-pink-hex",
                          })}`,
                          borderRadius: "0.5rem",
                          px: 2,
                          py: "2px",
                          mb: 1,
                          mx: "auto",
                          backgroundColor: alpha("#fff", 0.2),
                        }}
                        component={"span"}
                        textAlign={"center"}
                      >
                        No {CLIENT_EMAIL_LABEL?.[item]} Profile Added
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {values?.emails?.[key]?.map((e, i) => {
                        return (
                          <CustomChip
                            key={i}
                            label={
                              <CustomTooltip
                                title={
                                  <Box
                                    key={i}
                                    sx={{
                                      border: `1px solid ${getCssVariableHandler(
                                        {
                                          variableName: "--primary-pink-hex",
                                        },
                                      )}`,
                                      borderRadius: "0.5rem",
                                      p: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: isSmallScreen ? "0" : "1rem",
                                        width: "100%",
                                        flexWrap: "wrap",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-start",
                                          alignItems: "flex-start",
                                          gap: isSmallScreen
                                            ? "0.25rem"
                                            : "0.5rem",
                                          width: "100%",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            color: getCssVariableHandler({
                                              variableName:
                                                "--primary-pink-hex",
                                            }),
                                            fontWeight: "500",
                                          }}
                                        >
                                          Name:{" "}
                                        </Typography>
                                        <Typography>{e?.name || e?.[`${item}Email`]}</Typography>
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: isSmallScreen ? "0" : "1rem",
                                        width: "100%",
                                        flexWrap: "wrap",
                                        flexDirection: isSmallScreen
                                          ? "column"
                                          : "row",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-start",
                                          alignItems: "flex-start",
                                          gap: isSmallScreen
                                            ? "0.25rem"
                                            : "0.5rem",
                                          width: "100%",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            color: getCssVariableHandler({
                                              variableName:
                                                "--primary-pink-hex",
                                            }),
                                            fontWeight: "500",
                                          }}
                                        >
                                          Phone:{" "}
                                        </Typography>
                                        <Typography>
                                          {phoneNumberFormat(
                                            e?.[`${item}Mobile`],
                                          )}
                                        </Typography>
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: isSmallScreen ? "0" : "1rem",
                                        width: "100%",
                                        flexWrap: "wrap",
                                        flexDirection: isSmallScreen
                                          ? "column"
                                          : "row",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-start",
                                          alignItems: "flex-start",
                                          gap: isSmallScreen
                                            ? "0.25rem"
                                            : "0.5rem",
                                          width: isSmallScreen ? "100%" : "47%",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            color: getCssVariableHandler({
                                              variableName:
                                                "--primary-pink-hex",
                                            }),
                                            fontWeight: "500",
                                          }}
                                        >
                                          Email:{" "}
                                        </Typography>
                                        <Typography>
                                          {e?.[`${item}Email`]}{" "}
                                        </Typography>
                                      </Typography>
                                    </Box>
                                  </Box>
                                }
                              >
                                {e?.name || e?.[`${item}Email`]}
                              </CustomTooltip>
                            }
                            onDelete={
                              !isBillingReadOnly
                                ? () => {
                                    setValues((prev) => ({
                                      ...prev,
                                      emails: {
                                        ...prev.emails,
                                        [`${item}Emails`]: prev.emails[
                                          `${item}Emails`
                                        ].filter((_, index) => index !== i),
                                      },
                                    }));
                                  }
                                : null
                            }
                          />
                        );
                      })}
                    </Box>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
        {showClientEmailPrefs && (
          <ClientEmailPreferencesSection
            ref={emailPrefsRef}
            clientId={data?._id || data?.id}
          />
        )}
        {/* {((data?._id || data?.id) && */}
        {/*   isUserRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN)) || */}
        {/*   (isActiveRole(USER_ROLES.CLIENT) && values?.isBusinessProfile && ( */}
        {/*     <Typography textAlign={"center"}> */}
        {/*       NOTE: With edit of company and billing details it'll also edit for */}
        {/*       the clients with same company. */}
        {/*     </Typography> */}
        {/*   ))} */}

        {!(data?._id || data?.id) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "0.5rem",
              mt: 3,
            }}
          >
            <Checkbox
              name="sendemail"
              sx={{
                cursor: "pointer",
                padding: "0",
                color: getCssVariableHandler({
                  variableName: "--white-rgb",
                }),
                "&.Mui-checked": {
                  color: getCssVariableHandler({
                    variableName: "--primary-pink-hex",
                  }),
                  "& svg": {
                    fill: getCssVariableHandler({
                      variableName: "--primary-pink-hex",
                    }),
                  },
                },
              }}
              checked={!!sendEmail}
              onChange={(e) => {
                setSendEmail(e?.target?.checked);
              }}
            />
            <Typography>
              Send an email to client informing account creation
            </Typography>
          </Box>
        )}

        <Box className="bottom-btn-box">
          <Button
            className="btn-black"
            onClick={() => {
              formik.setValues(initialValues);
              handleClose();
            }}
          >
            <Typography className="btn-text">CLOSE</Typography>
          </Button>
          <Button
            sx={{
              cursor: isLoading || formik.isSubmitting ? "not-allowed" : "pointer",
            }}
            disableRipple={isLoading || formik.isSubmitting}
            className="btn-pink"
            onClick={() => {
              if (isLoading || formik.isSubmitting) return;
              handleSubmit();
            }}
          >
            {isLoading || formik.isSubmitting ? (
              <CircularProgress />
            ) : (
              <Typography className="btn-text">Submit</Typography>
            )}
          </Button>
        </Box>
        {/* NOTE: we'll use the merge on assign flow */}
        <MergeClientBilling
          open={showMergeModal}
          onClose={() => {
            setShowMergeModal(false);
          }}
          providedData={providedDataValue}
          companyData={values}
          formik={formik}
          onSaveClick={() => {}}
          preSelectedCompany={{
            isSameAddress: values?.isSameAddress,
            companyName: values?.companyName,
            clientDetails: {
              COIAddress: values?.COIAddress,
              companyAddress: values?.companyAddress,
              emails: values?.emails,
            },
          }}
        />
      </Box>
    </Modal>
  );
}

AddClientModal.propTypes = {
  heading: PropTypes.string,
  open: PropTypes.bool,
  setEditingData: PropTypes.func,
  setOpen: PropTypes.func,
  data: PropTypes.object,
  isProfile: PropTypes.bool,
  afterSaveCall: PropTypes.func,
  providedData: PropTypes.any,
};
